/**
 * Reference Feedback Service
 * Handles API calls for submitting feedback on quotes and images from Content Fabric
 */

import { FEEDBACK } from '../constants/apiRoutes';
import {
  ReferenceFeedbackPayload,
  ReferenceFeedbackResponse,
  ImageFeedbackPayload,
} from '../types/ReferenceFeedback';
import { httpClient, HTTPClientRequestPayload } from '../utils/httpClient';
import Logger from '../utils/logger';

interface ApiError {
  message: string;
}

/**
 * Submit feedback for a quote reference
 * @param payload - The feedback payload containing quote details and user feedback
 * @returns Promise with success status
 */
export const submitReferenceFeedback = async (
  payload: ReferenceFeedbackPayload
): Promise<ReferenceFeedbackResponse> => {
  const requestPayload: HTTPClientRequestPayload = {
    ...payload,
  };

  try {
    const response = await httpClient.post<ReferenceFeedbackResponse>(
      FEEDBACK,
      requestPayload
    );

    const { success, data } = response as ReferenceFeedbackResponse;

    if (success) {
      return { success: true, data };
    }

    return { success: false };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(
      new Error(`Reference feedback submission failed: ${postError.message}`)
    );
    return { success: false, message: postError.message };
  }
};

/**
 * Submit feedback for an image
 * @param payload - The feedback payload containing image details and user feedback
 * @returns Promise with success status
 */
export const submitImageFeedback = async (
  payload: ImageFeedbackPayload
): Promise<ReferenceFeedbackResponse> => {
  const requestPayload: HTTPClientRequestPayload = {
    ...payload,
  };

  try {
    const response = await httpClient.post<ReferenceFeedbackResponse>(
      FEEDBACK,
      requestPayload
    );

    const { success, data } = response as ReferenceFeedbackResponse;

    if (success) {
      return { success: true, data };
    }

    return { success: false };
  } catch (error) {
    const postError = error as ApiError;
    Logger.error(
      new Error(`Image feedback submission failed: ${postError.message}`)
    );
    return { success: false, message: postError.message };
  }
};
